import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Chip,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

// ============================================================================
// COOKIE POLICY PAGE
// Simple cookie policy - we only use essential cookies for authentication
// ============================================================================

const CookiePolicy: React.FC = () => {
  const lastUpdated = 'December 8, 2025';
  const companyName = 'Centre Of Business';
  const appName = 'Check Set Go';
  const contactEmail = 'support@checksetgo.com';

  // Section component for consistent styling
  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  // Cookie/storage data - only essential items
  const storageItems = [
    {
      name: 'audit-platform-auth',
      type: 'Essential',
      purpose: 'Authentication session data (Supabase)',
      duration: 'Session / 7 days',
      provider: 'Supabase',
    },
    {
      name: 'cookie_consent',
      type: 'Essential',
      purpose: 'Records that you acknowledged our cookie notice',
      duration: '12 months',
      provider: 'Check Set Go',
    },
    {
      name: 'cookie_consent_date',
      type: 'Essential',
      purpose: 'Records when you acknowledged the notice',
      duration: '12 months',
      provider: 'Check Set Go',
    },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, border: '1px solid #E0E0E0' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CookieIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Cookie Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Section title="1. What Are Cookies?">
          <Typography paragraph>
            Cookies and local storage are small pieces of data stored on your device when you visit 
            a website. They help websites remember information about your visit, like your login status.
          </Typography>
          <Typography paragraph>
            {appName} uses <strong>only essential cookies and local storage</strong> required for the 
            platform to function. We do not use tracking, analytics, or marketing cookies.
          </Typography>
        </Section>

        {/* What We Use */}
        <Section title="2. What We Use">
          <Typography paragraph>
            We use browser local storage for authentication and to remember that you've seen our 
            cookie notice. All storage we use is <strong>strictly necessary</strong> for the platform 
            to work:
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storageItems.map((item) => (
                  <TableRow key={item.name}>
                    <TableCell>
                      <code style={{ fontSize: '0.85rem' }}>{item.name}</code>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.type} 
                        size="small" 
                        color="error"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.purpose}</TableCell>
                    <TableCell>{item.duration}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ 
            p: 2, 
            bgcolor: 'success.light', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.main',
            opacity: 0.9
          }}>
            <Typography variant="body2">
              <strong>No tracking:</strong> We do not use Google Analytics, Facebook Pixel, or any 
              other tracking or advertising technologies. Your browsing activity is not monitored 
              or shared with third parties for advertising purposes.
            </Typography>
          </Box>
        </Section>

        {/* Third-Party Services */}
        <Section title="3. Third-Party Services">
          <Typography paragraph>
            Our platform integrates with the following services that may store data:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Supabase (Authentication)
            </Typography>
            <Typography paragraph>
              We use Supabase for user authentication. Supabase stores session data in your browser's 
              local storage to keep you logged in. For more information, see{' '}
              <Link href="https://supabase.com/privacy" target="_blank" rel="noopener">
                Supabase's Privacy Policy
              </Link>.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Stripe (Payments)
            </Typography>
            <Typography paragraph>
              When you make a payment, you are redirected to Stripe's secure checkout. Stripe may 
              set cookies on their domain to process your transaction securely and prevent fraud. 
              For more information, see{' '}
              <Link href="https://stripe.com/privacy" target="_blank" rel="noopener">
                Stripe's Privacy Policy
              </Link>.
            </Typography>
          </Box>
        </Section>

        {/* Managing Storage */}
        <Section title="4. Managing Browser Storage">
          <Typography paragraph>
            You can clear local storage and cookies through your browser settings:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener">
              Google Chrome
            </Link>
            <Link href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener">
              Mozilla Firefox
            </Link>
            <Link href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener">
              Safari
            </Link>
            <Link href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener">
              Microsoft Edge
            </Link>
          </Box>

          <Box sx={{ 
            p: 2, 
            bgcolor: 'warning.light', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main'
          }}>
            <Typography variant="body2">
              <strong>Note:</strong> If you clear your browser's local storage, you will be logged 
              out and will need to sign in again.
            </Typography>
          </Box>
        </Section>

        {/* Updates to Policy */}
        <Section title="5. Updates to This Policy">
          <Typography paragraph>
            We may update this Cookie Policy if we change how we use browser storage. When we make 
            changes, we will update the "Last updated" date at the top of this page.
          </Typography>
          <Typography paragraph>
            If we ever introduce non-essential cookies (such as analytics), we will update this 
            policy and request your consent before using them.
          </Typography>
        </Section>

        {/* Contact */}
        <Section title="6. Contact Us">
          <Typography paragraph>
            If you have any questions about our use of cookies or this policy, please contact us:
          </Typography>
          <Box sx={{ 
            p: 3, 
            bgcolor: 'grey.50', 
            borderRadius: 2, 
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{companyName}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>
            </Box>
          </Box>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* Footer */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} {companyName}. All rights reserved.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Link href="/privacy-policy" color="primary">Privacy Policy</Link>
            <Link href="/terms-of-service" color="primary">Terms of Service</Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CookiePolicy;
